import {
	FileCheck2,
	FileSearch,
	GitMerge,
	History,
	Network,
	ScrollText,
	Search,
	Users,
} from "lucide-react";
import {
	type SegmentLandingConfig,
	SegmentLandingPage,
} from "~/components/marketing/segment-landing-page";
import { generateMetaTags, SITE_URL } from "~/lib/seo";

export function meta() {
	return generateMetaTags({
		title: "FirmIndex pro právníky a insolvenční správce",
		description:
			"Firemní události, pravidla ze stanov a vztahy uvedené ve zprávách o vztazích v přehledné podobě se zdrojovou listinou.",
		canonical: `${SITE_URL}/pro-pravniky-a-insolvencni-spravce`,
		noindex: true,
	});
}

const config = {
	eyebrow: "Pro právní, insolvenční a transakční praxi",
	titleLead: "Neprocházejte jen seznam listin.",
	titleHighlight: "Pochopte, co v nich firma zveřejnila.",
	description:
		"FirmIndex převádí obsah notářských zápisů, projektů přeměn, stanov a zpráv o vztazích do strukturovaných událostí a souvislostí.",
	heroNote:
		"Rozsah závisí na zveřejněných dokumentech. Výstup je informační podklad a nenahrazuje právní posouzení ani úplnou due diligence.",
	proofPoints: ["14 dní Premium", "Vazba na zdrojovou listinu", "Historie firemních událostí"],
	preview: {
		overline: "Podklady k případu",
		title: "Co dokumenty říkají o firmě",
		subtitle: "Strukturovaný vstup do rešerše před čtením původních listin.",
		badge: "3 souvislosti",
		items: [
			{
				icon: GitMerge,
				label: "Projekt přeměny",
				value: "Zachycená firemní událost",
			},
			{
				icon: ScrollText,
				label: "Stanovy společnosti",
				value: "Způsob jednání a pravidla správy",
			},
			{
				icon: Network,
				label: "Zpráva o vztazích",
				value: "Vztahy uvedené v dokumentu",
			},
		],
	},
	valueOverline: "Rychlejší orientace v podkladech",
	valueHeading: "Začněte u souvislostí a k rozhodným údajům se vraťte ve zdroji",
	valueDescription:
		"FirmIndex pomáhá zkrátit první fázi firemní rešerše. Ukáže, které dokumenty a události si zaslouží podrobné odborné posouzení.",
	valueCards: [
		{
			icon: FileSearch,
			title: "Předběžná rešerše protistrany",
			description:
				"Spojte registrační historii, osoby, finance, insolvenci a zdrojové dokumenty do jednoho výchozího bodu.",
		},
		{
			icon: History,
			title: "Chronologie firemních změn",
			description:
				"Fúze, změny kapitálu, likvidace nebo změny orgánů uvidíte v časové souvislosti s firmou.",
		},
		{
			icon: Network,
			title: "Vztahy popsané ve skupině",
			description:
				"Ze zveřejněných zpráv o vztazích zpřehledňujeme uvedené ovládající a propojené osoby a významné vztahy.",
		},
	],
	workflowHeading: "Od subjektu přes událost až k původní listině",
	workflowDescription:
		"Strukturovaný přehled slouží jako rozcestník. Rozhodné skutečnosti můžete následně ověřit přímo v dokumentech a příslušných registrech.",
	workflowSteps: [
		{
			icon: Search,
			title: "Vyhledejte firmu nebo protistranu",
			description:
				"Začněte názvem nebo IČO a získejte jednotný přehled dostupných veřejných podkladů.",
		},
		{
			icon: GitMerge,
			title: "Projděte události a vztahy",
			description:
				"Zaměřte se na přeměny, změny kapitálu, pravidla správy nebo vztahy uvedené ve zveřejněných zprávách.",
		},
		{
			icon: FileCheck2,
			title: "Ověřte rozhodný údaj ve zdroji",
			description:
				"Vraťte se k původní listině a použijte ji spolu s dalšími podklady pro vlastní odborné posouzení.",
		},
	],
	depthOverline: "Dokumentová inteligence FirmIndexu",
	depthHeading: "Detaily, které základní výpis obchodního rejstříku neukáže",
	depthDescription:
		"Nefinanční listiny mohou popsat budoucí přeměnu, vnitřní pravidla společnosti i deklarované vztahy ve skupině.",
	depthCards: [
		{
			icon: GitMerge,
			title: "Notářské zápisy a projekty přeměn",
			description:
				"Z dokumentů strukturalizujeme zveřejněné informace o fúzích, rozděleních, kapitálu, likvidaci nebo změnách orgánů.",
		},
		{
			icon: ScrollText,
			title: "Stanovy a pravidla správy",
			description:
				"Způsob jednání, převoditelnost podílů, předkupní práva, hlasování nebo pravidla rozdělení zisku na jednom místě.",
		},
		{
			icon: Users,
			title: "Zprávy o vztazích",
			description:
				"Ovládající a propojené osoby, významné smluvní vztahy a vyjádření k případné újmě podle obsahu zveřejněné zprávy.",
		},
	],
	legalHeading: "Rešeršní podklad, ne právní závěr",
	legalDescription:
		"Automatická extrakce pomáhá najít relevantní pasáže a souvislosti, ale může obsahovat chyby nebo nezachytit celý význam dokumentu.",
	legalNote:
		"FirmIndex nenahrazuje právní stanovisko, úplnou due diligence ani úřední výpis vlastnictví či majetku. Podstatné závěry vždy ověřte v původní listině a v dalších rozhodných zdrojích.",
	trialHeading: "Vyzkoušejte rešerši na konkrétní firmě zdarma",
	trialDescription:
		"Otevřete firmu z vlastní praxe a posuďte, zda vám strukturované události a vztahy urychlí orientaci v dokumentech. Platební kartu nepotřebujete.",
} satisfies SegmentLandingConfig;

export default function ProPravnikyAInsolvencniSpravce() {
	return <SegmentLandingPage config={config} />;
}
